/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { GridLayoutItem, GridsterItem } from '@app/core/types';
import {
  GRID_LAYOUT_GAP,
  GRID_LAYOUT_ITEM_MIN_ROWS,
  GRID_LAYOUT_MAX_ROWS,
  GRID_LAYOUT_MIN_ROW_HEIGHT,
  GRID_LAYOUT_VISIBLE_ROWS,
} from '@common/components/widgets-dashboard/constants';
import { KtdGridLayout } from '@katoid/angular-grid-layout';
import { maxBy } from 'lodash-es';

export const toGridLayout = (gridsterItem: GridsterItem): GridLayoutItem => ({
  x: gridsterItem.x,
  y: gridsterItem.y,
  w: gridsterItem.cols,
  h: gridsterItem.rows,
});

export const toGridsterItem = (layoutItem: GridLayoutItem): GridsterItem => ({
  cols: layoutItem.w,
  rows: layoutItem.h,
  x: layoutItem.x,
  y: layoutItem.y,
});

export const getRowHeight = (gridHeight: number): number => {
  const gapTotalHeight = (GRID_LAYOUT_VISIBLE_ROWS - 1) * GRID_LAYOUT_GAP;
  const gridHeightWithoutGap = gridHeight - gapTotalHeight;
  const rowHeight = Math.round(gridHeightWithoutGap / GRID_LAYOUT_VISIBLE_ROWS);
  return rowHeight < GRID_LAYOUT_MIN_ROW_HEIGHT ? GRID_LAYOUT_MIN_ROW_HEIGHT : rowHeight;
};

// change the height of grid items if it exceeds the GRID_LAYOUT_MAX_ROWS
// The current position does not need to be changed, because ktgGrid will do it itself
export const resizeItemsHeight = (layout: KtdGridLayout): boolean => {
  let layoutChanged = false;
  const bottomGridItem = maxBy(layout, (it) => it.y + it.h);
  if (bottomGridItem) {
    const rowCount = bottomGridItem.y + bottomGridItem.h;
    if (rowCount > GRID_LAYOUT_MAX_ROWS) {
      layoutChanged = true;
      const coefficient = rowCount / GRID_LAYOUT_MAX_ROWS;
      layout.forEach((it) => {
        const minH = it.minH ?? GRID_LAYOUT_ITEM_MIN_ROWS;
        const newH = Math.ceil(it.h / coefficient);
        it.h = newH > minH ? newH : minH;
      });
    }
  }
  return layoutChanged;
};

export function sortLayoutItems(layout: GridLayoutItem[]): GridLayoutItem[] {
  return layout.sort((a, b) => {
    if (a.y > b.y || (a.y === b.y && a.x > b.x)) {
      return 1;
    } else if (a.y === b.y && a.x === b.x) {
      // Without this, we can get different sort results in IE vs. Chrome/FF
      return 0;
    }
    return -1;
  });
}
